import { Color } from "@playlive/overlay-core/colors";
import {
  MilestoneOverlay,
  MilestoneOverlayBackgroundColors,
  toMilestonesOverlayBackgroundColor,
} from "@playlive/overlay-core/overlays/milestone";
import { deepSignal } from "deepsignal";
import { useCallback, useEffect, useMemo } from "preact/hooks";
import Picker from "@/components/input/picker.input.jsx";
import CustomizeSection from "@/components/overlays/shared/customize-section.jsx";
import LinkSetup from "@/components/overlays/social/link-setup/link-setup.jsx";
import HorizontalCustomizeWrapper from "@/components/overlays/wrapper/horizonal-customize-wrapper.jsx";
import ScaledOverlayWrapper from "@/components/overlays/wrapper/scaled-overlay-wrapper.jsx";
import ColorPicker from "@/components/pickers/color-picker/color.picker.jsx";
import ColorPickerLabel from "@/components/pickers/color-picker/color-picker-label.jsx";
import useCommon from "@/hooks/useCommon.js";
import useDemoProgressBarData from "@/hooks/useDemoProgressBarData.js";
import { isYes } from "@/util/common";
import { CHARITY } from "@/util/const.js";

const config = deepSignal({
  charityType: CHARITY.TILTIFY,
  displayTimer: "0",
  bgColor: Color.DEFAULT_RED,
  visual: "rounded",
});

export default function MilestoneOverlayPage() {
  const useBetaOverlays = isYes(import.meta.env.VITE_BETA_OVERLAYS || "no");
  const { overlayInfo, overlayURL, setConfigState } = useCommon({
    config,
    forceTiltify: true,
    urlBase: useBetaOverlays ? "overlays/milestones" : "milestones",
  });

  const { demoMilestones } = useDemoProgressBarData(config);

  const filterColors = useCallback((color) => {
    return MilestoneOverlayBackgroundColors.find((c) => c === color);
  }, []);

  useEffect(() => {
    if (![...MilestoneOverlayBackgroundColors].includes(config.bgColor)) {
      setConfigState({ target: { name: "bgColor", value: "default-red" } });
    }
  }, [setConfigState]);

  const timerOptions = useMemo(
    () => [
      {
        value: "5",
        name: "Five minutes",
      },
      {
        value: "10",
        name: "Ten minutes",
      },
      {
        value: "15",
        name: "Fifteen minutes",
      },
      {
        value: "0",
        name: "Always",
      },
    ],
    [],
  );

  const themeOptions = useMemo(
    () => [
      {
        name: "Slanted",
        value: "slanted",
      },
      {
        name: "Rounded",
        value: "rounded",
      },
    ],
    [],
  );

  return (
    <div className="w-full overflow-x-hidden">
      <div className="flex flex-col xl:flex-row overflow-x-hidden">
        <div className="example_overlay select-none overflow-x-hidden w-full">
          <ScaledOverlayWrapper justifyCenter={false} allowScaling={false}>
            <MilestoneOverlay
              currentAmount={350}
              goalAmount={1000}
              theme={config.visual}
              displayTimer={config.displayTimer}
              milestones={demoMilestones}
              backgroundColor={toMilestonesOverlayBackgroundColor(config.bgColor)}
              type={config.visual}
            />
          </ScaledOverlayWrapper>
        </div>

        <HorizontalCustomizeWrapper className="flex flex-col mb-0!">
          <CustomizeSection title="Appearance">
            <ColorPickerLabel color={config.bgColor} />

            <ColorPicker
              fieldName="bgColor"
              currentColor={config.bgColor}
              filterFunction={filterColors}
              onChange={setConfigState}
            />

            <Picker
              items={themeOptions}
              fieldName="visual"
              onChange={setConfigState}
              currentValue={config.visual}
              labelText="Theme"
            />
          </CustomizeSection>

          <CustomizeSection title="Misc." className="grow">
            <Picker
              items={timerOptions}
              fieldName="displayTimer"
              onChange={setConfigState}
              currentValue={config.displayTimer}
              labelText="Display Timer"
              // description="Set amount of time to rotate future milestones visibility in and out on the overlay"
              description="Set how often future milestones automatically appear and disappear, or keep them always visible."
            />
          </CustomizeSection>

          {/* Charity Section Placeholder */}
        </HorizontalCustomizeWrapper>
      </div>

      <LinkSetup
        constrainWidth={false}
        charityType={CHARITY.TILTIFY}
        overlayName={overlayInfo.name}
        overlayURL={overlayURL}
      />
    </div>
  );
}
